import { createContext, useContext, useEffect, useState } from "react";
import { http } from "@/lib/api";

const SYMBOL = { NGN: "₦", USD: "$", GBP: "£", EUR: "€", CAD: "C$" };
const MULT = { b: 1e9, bn: 1e9, billion: 1e9, m: 1e6, million: 1e6, k: 1e3, thousand: 1e3 };

// '$450,000' -> {value: 450000, cur: 'USD'}; unmarked prices are treated as naira.
export const parseMoney = (text) => {
  if (!text) return null;
  const t = String(text).replace(/,/g, "");
  const m = t.match(/(\d+(?:\.\d+)?)\s*(bn|b|billion|m|million|k|thousand)?/i);
  if (!m) return null;
  const low = t.toLowerCase();
  const cur = /c\$|ca\$|cad|canadian/.test(low) ? "CAD" : /€|eur/.test(low) ? "EUR"
    : /£|gbp|pound/.test(low) ? "GBP" : /\$|usd|dollar/.test(low) ? "USD" : "NGN";
  return { value: parseFloat(m[1]) * (MULT[(m[2] || "").toLowerCase()] || 1), cur };
};

export const CURRENCY_SYMBOL = SYMBOL;

export const formatMoney = (value, cur) => {
  const compact = value >= 1e7;
  const n = new Intl.NumberFormat("en", compact ? { notation: "compact", maximumFractionDigits: 1 } : { maximumFractionDigits: 0 }).format(value);
  return `${SYMBOL[cur]}${n}`;
};

const CurrencyContext = createContext({ currency: "original", setCurrency: () => {}, rates: null, show: (t) => t });

export function CurrencyProvider({ children }) {
  const [currency, setCur] = useState(() => localStorage.getItem("gw_currency") || "original");
  const [rates, setRates] = useState(null);
  useEffect(() => { http.get("/rates").then((r) => setRates(r.data.rates)).catch(() => setRates(null)); }, []);
  const setCurrency = (c) => { localStorage.setItem("gw_currency", c); setCur(c); };

  // Returns { text, converted } for a free-text price.
  const show = (price) => {
    if (currency === "original" || !rates) return { text: price, converted: false };
    const p = parseMoney(price);
    if (!p) return { text: price, converted: false };
    if (p.cur === currency) return { text: price, converted: false };
    const value = (p.value / rates[p.cur]) * rates[currency];
    return { text: `≈ ${formatMoney(value, currency)}`, converted: true, original: price };
  };
  return <CurrencyContext.Provider value={{ currency, setCurrency, rates, show }}>{children}</CurrencyContext.Provider>;
}

export const useCurrency = () => useContext(CurrencyContext);

export const Price = ({ price, className = "", testId }) => {
  const { show } = useCurrency();
  if (!price) return null;
  const r = show(price);
  return <span className={className} title={r.converted ? `Listed as ${r.original}` : undefined} data-testid={testId}>{r.text}</span>;
};
